import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MapMarker, MapView, MAP_MIN_ZOOM, tangerRasterSource, type MapViewport } from '@wehunt/map';
import { TeamMapLayers } from './TeamMapLayers';
import { useHuntMapAppearance } from '../lib/useHuntMapAppearance';
import { useBaseMapTileInfo } from '../lib/useBaseMapTileInfo';
import { EMPTY_MAP_VIEW, groundFramePoints } from '../lib/mapSeedView';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { RSVP_COLORS } from '../lib/rsvpPresentation';
import type { Hunt, RsvpStatus, Participant, HuntPlanParticipant, Game, TeamDoc, TeamMap, HuntPlanDetail, HuntPlanAttachmentFile, HuntPlanTeamDocument, GeoPt, MapObject, GamePinView, GamePinInput, GamePinType, GamePinGender, GamePinAgeCategory, GamePinGameType } from '../lib/types';
import { type HuntableGameSpecies, selectableDownedGameSpecies, speciesDisplayName } from '../lib/huntable-game-catalog';
import { huntDisplayName } from '../lib/huntTitle';
import { viewerLeadsHunt } from '../lib/huntLeaders';
import { uiLocale } from '../lib/dateFormat';
import { DeltaRenderer } from './DeltaRenderer';
import { DocIcon } from './DocIcon';
import { FilePreviewPopup } from './FilePreviewPopup';
import { ConfirmModal } from './ConfirmModal';
import { Icon } from './Icon';
import { LiveDot } from './LiveDot';
import { Avatar } from './atoms';

// ── Inline sample data ────────────────────────────────────────────────────

const _isoOffset = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// A subset of the prototype's `lib/data.ts` SAMPLE_HUNTS, backing the detail
// view. `location` (the hunt's name — see huntDisplayName) must match the entry
// there for the same id: the list and calendar read lib/data.ts, this view reads
// here, and a mismatch has the list call a hunt one thing and its own header
// another.
const SAMPLE_HUNTS: Hunt[] = [
  { id: 'h1',  date: _isoOffset(-42), start: '06:30', end: '14:00', team: 'ANGERMANLAND',     teamId: 't1', quarry: 'Moose',     location: 'Saturday moose drive — north parcel',     participants: 9, dogs: ['Aksel', 'Freja'], status: 'ended', harvest: [{ species: 'Moose', count: 1, note: 'Young bull' }] },
  { id: 'h2',  date: _isoOffset(-35), start: '07:00', end: '12:30', team: 'ANGERMANLAND',     teamId: 't1', quarry: 'Moose',     location: '',     participants: 8, dogs: ['Aksel'],          status: 'ended', harvest: [] },
  { id: 'h9',  date: _isoOffset(2),   start: '07:00', end: '13:00', team: 'ANGERMANLAND',     teamId: 't1', quarry: 'Moose',     location: 'Autumn opener',     participants: 10, dogs: ['Aksel', 'Freja'], status: 'upcoming', myRsvp: 'pending',  invitedBy: 'Anders Jarl' },
  { id: 'h10', date: _isoOffset(3),   start: '06:30', end: '14:00', team: 'ANGERMANLAND',     teamId: 't1', quarry: 'Moose',     location: '',     participants: 9,  dogs: ['Aksel'],          status: 'upcoming', myRsvp: 'accepted' },
  { id: 'h11', date: _isoOffset(4),   start: '06:00', end: '15:00', team: 'Kalix Moose Hunt', teamId: 't2', quarry: 'Moose',     location: 'Kalix river west', participants: 8,  dogs: ['Bjorn'],          status: 'upcoming', myRsvp: 'pending',  invitedBy: 'Petra Lundgren' },
  // CLUB-580 — a declined hunt, mirroring `h15` in the prototype's lib/data.ts.
  // Without it the declined detail view had no fixture behind it, so opening a
  // declined hunt from the list fell through to `return null` (a blank page)
  // and the state the ticket is about could not be seen in the prototype.
  { id: 'h15', date: _isoOffset(24),  start: '20:00', end: '23:30', team: 'Smaland Wild Boar', teamId: 't3', quarry: 'Wild boar', location: 'Late boar',   participants: 5,  dogs: ['Saga'],           status: 'upcoming', myRsvp: 'declined', invitedBy: 'Sven Karlsson' },
  { id: 'h-live', date: _isoOffset(0), start: '07:00', end: '13:00', team: 'ANGERMANLAND',     teamId: 't1', quarry: 'Moose',     location: 'Sunday moose drive',     participants: 9,  dogs: ['Aksel', 'Freja'], status: 'ongoing',  myRsvp: 'accepted' },
];

const SAMPLE_TEAMS_MAP: Record<string, TeamMap> = {
  t1: {
    name: 'North parcel',
    updated: '2 days ago',
    boundaryAreas: [{ id: 'b1', name: '', rings: [[
      { lat: 63.3352, lon: 17.7348 }, { lat: 63.3417, lon: 17.8219 }, { lat: 63.3352, lon: 17.9236 },
      { lat: 63.3156, lon: 17.9962 }, { lat: 63.2765, lon: 18.0252 }, { lat: 63.2309, lon: 18.0107 },
      { lat: 63.1983, lon: 17.9526 }, { lat: 63.1918, lon: 17.8510 }, { lat: 63.2048, lon: 17.7638 },
      { lat: 63.2439, lon: 17.7203 }, { lat: 63.2961, lon: 17.7203 },
    ]]}],
    objects: [
      { id: 'o1', type: 'tower', lat: 63.3026, lon: 17.8001, label: 'Stand 1 – North edge' },
      { id: 'o2', type: 'tower', lat: 63.2896, lon: 17.8945, label: 'Stand 2 – The marsh' },
      { id: 'o3', type: 'tower', lat: 63.2504, lon: 17.8510, label: 'Stand 3 – Clearing' },
      { id: 'o4', type: 'tower', lat: 63.2374, lon: 17.9526, label: 'Stand 4 – The bend' },
    ],
  },
  // A boundary with no pins, backing h15. It exists to give the drive map
  // preview a real (if pin-less) ground to frame — the drive-stored-only-a-
  // count-but-no-per-stand-geometry case, which needs a team map behind it to
  // be reachable at all: without one `DrivesTab` never mounts the preview.
  t3: {
    name: 'Boar covert',
    updated: '5 days ago',
    boundaryAreas: [{ id: 'b3', name: '', rings: [[
      { lat: 56.6021, lon: 12.8956 }, { lat: 56.6104, lon: 12.9520 }, { lat: 56.5834, lon: 12.9312 },
    ]]}],
    objects: [],
  },
};

const PARTICIPANT_POOL: Participant[] = [
  { id: 'p-erik',   name: 'Erik Lindqvist', role: 'owner',  avatar: 'EL', color: '#20502b', photo: 'user-1.png',  you: true },
  { id: 'p-anna',   name: 'Anna Sjoberg',   role: 'admin',  avatar: 'AS', color: '#8a6d3a', photo: 'user-2.png' },
  { id: 'p-nils',   name: 'Nils Berg',      role: 'member', avatar: 'NB', color: '#6b4423', photo: 'user-3.png' },
  { id: 'p-maja',   name: 'Maja Holm',      role: 'member', avatar: 'MH', color: '#c9a572', photo: 'user-4.png' },
  { id: 'p-johan',  name: 'Johan Ek',       role: 'member', avatar: 'JE', color: '#3a5a3f', photo: 'user-5.png' },
  { id: 'p-lina',   name: 'Lina Persson',   role: 'member', avatar: 'LP', color: '#a03c16', photo: 'user-6.png' },
  { id: 'p-oskar',  name: 'Oskar Dahl',     role: 'member', avatar: 'OD', color: '#4a5a3a', photo: 'user-7.png' },
  { id: 'p-petra',  name: 'Petra Lundgren', role: 'member', avatar: 'PL', color: '#747a76' },
  { id: 'p-sven',   name: 'Sven Karlsson',  role: 'member', avatar: 'SK', color: '#5a3a2a', photo: 'user-8.png' },
  { id: 'p-mats',   name: 'Mats Eriksson',  role: 'member', avatar: 'ME', color: '#3a4a5a', photo: 'user-9.png' },
];

const HUNTABLE_GAME: Game[] = [
  { id: 'moose', name: 'Moose',     season: 'Sep 1 – Jan 31',  color: '#20502b', classes: ['Calf', 'Heifer', 'Cow', 'Young bull', 'Bull'] },
  { id: 'roe',   name: 'Roe deer',  season: 'May 1 – Jan 31',  color: '#8a6d3a', classes: ['Kid', 'Doe', 'Buck'] },
  { id: 'boar',  name: 'Wild boar', season: 'Year round',      color: '#ff681f', classes: ['Piglet', 'Sow', 'Boar'] },
];

// Sample documents for the prototype's mock hunts, alongside SAMPLE_HUNTS and
// HUNTABLE_GAME. A real hunt never resolves against these — its host passes the
// team's actual documents in as `teamDocuments`. They carry no storagePath, so
// their chips stay unclickable: there is nothing behind them to open.
const SAMPLE_TEAM_DOCS: HuntPlanTeamDocument[] = [
  { id: 'd-lease-2025', name: 'Hunting ground lease 2025.pdf', kind: 'pdf' },
  { id: 'd-safety',     name: 'Safety regulations.pdf',        kind: 'pdf' },
  { id: 'd-bylaws',     name: 'Team by-laws.docx',             kind: 'doc' },
  { id: 'd-schedule',   name: 'Season schedule.xlsx',          kind: 'xls' },
  { id: 'd-map',        name: 'Hunting ground map.png',        kind: 'img' },
];

// ── Types ─────────────────────────────────────────────────────────────────

/**
 * Translate a backend invite-response `status` string into the UI's RsvpStatus.
 *
 * The authoritative source is the `responses` map on huntPlans/{id}, where each
 * entry's `status` is a plain string the backend writes as "accepted" |
 * "rejected" (see HuntInvitationResponse). It is NOT a closed enum, so we
 * compare defensively and treat anything we don't recognise as "pending"
 * rather than leaking a raw value into the UI. Legacy "declined" is also
 * accepted as a synonym for "rejected".
 */
export function normalizeRsvpStatus(status: string | null | undefined): RsvpStatus {
  const s = status?.trim().toLowerCase();
  if (s === 'accepted') return 'accepted';
  if (s === 'rejected' || s === 'declined') return 'declined';
  return 'pending';
}

/**
 * Human-readable roster label for a participant's response. Returns null when
 * there is no response to show (the aggregate map simply has no entry for that
 * user yet), otherwise a normalised label so unexpected backend strings never
 * render verbatim.
 */
export function rsvpDisplayLabel(status: string | null | undefined): string | null {
  if (status == null) return null;
  const norm = normalizeRsvpStatus(status);
  return norm === 'accepted' ? 'Attending' : norm === 'declined' ? 'Declined' : 'Pending answer';
}

// The viewer's own 2nd-person banner. Colours come from the shared RSVP palette
// (RSVP_COLORS) so accepted/declined/pending never drift apart; only the label
// is local, and it is resolved at render time via i18next (labelKey).
const RSVP_TONE: Record<RsvpStatus, { color: string; bg: string; labelKey: string }> = {
  pending:  { color: RSVP_COLORS.pending.fg,  bg: RSVP_COLORS.pending.bg,  labelKey: 'rsvp.awaitingYourReply' },
  accepted: { color: RSVP_COLORS.accepted.fg, bg: RSVP_COLORS.accepted.bg, labelKey: 'rsvp.attending' },
  declined: { color: RSVP_COLORS.declined.fg, bg: RSVP_COLORS.declined.bg, labelKey: 'rsvp.youDeclined' },
};

// Roster-facing styling for each RSVP state. Same palette as RSVP_TONE — the
// two differ only in wording (3rd-person roster labels vs the viewer's own
// 2nd-person banner). Every participant row shows one of these: non-responders
// read "Pending answer" (the wording the mobile app uses for an unanswered
// invite) rather than a blank.
const PARTICIPANT_RSVP_STYLE: Record<RsvpStatus, { labelKey: string; color: string; bg: string }> = {
  pending:  { labelKey: 'rsvp.pending',   color: RSVP_COLORS.pending.fg,  bg: RSVP_COLORS.pending.bg },
  accepted: { labelKey: 'rsvp.attending', color: RSVP_COLORS.accepted.fg, bg: RSVP_COLORS.accepted.bg },
  declined: { labelKey: 'rsvp.declined',  color: RSVP_COLORS.declined.fg, bg: RSVP_COLORS.declined.bg },
};

const fmtDateLong = (iso: string, locale: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Header schedule line. A same-day hunt collapses to one date with a time
 * range; a hunt that ends on a different day spells out both full dates with
 * their times — so the exact ended date is always unambiguous (incl. an ended
 * hunt that ran past midnight).
 */
function fmtSchedule(plan: { date: string; endDate: string; startTime: string; endTime: string }, locale: string): string {
  if (!plan.endDate || plan.endDate === plan.date) {
    return `${fmtDateLong(plan.date, locale)} · ${plan.startTime}–${plan.endTime}`;
  }
  return `${fmtDateLong(plan.date, locale)}, ${plan.startTime} → ${fmtDateLong(plan.endDate, locale)}, ${plan.endTime}`;
}

type EnrichedPlan = {
  teamMap: TeamMap | null;
  teamColor: string;
  name: string;
  teamName: string;
  teamId: string;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
  leaders: string[];
  participantIds: string[];
  invitationText: string;
  attachedTeamDocIds: string[];
  attachmentFiles: HuntPlanAttachmentFile[];
  // A drive's time window, where one exists. Only the mock plans have it: a
  // stored drive carries no time of its own on either platform.
  drives: { id: string; name: string; start?: string; end?: string; area: string; standCount: number; stands: { id: string; pinId: string; assignedId: string | null; name?: string | null }[] }[];
  gameIds: string[];
  // Chosen classes per species, each with its own quota (limit null = no limit).
  gameClasses: Record<string, { name: string; limit: number | null }[]>;
  joinCode: string;
};

const enrichHuntPlan = (hunt: Hunt): EnrichedPlan => {
  const game = HUNTABLE_GAME.find((g) => g.name === hunt.quarry) || HUNTABLE_GAME[0];
  const teamMap = SAMPLE_TEAMS_MAP[hunt.teamId] ?? null;
  const towers = teamMap ? teamMap.objects.filter((o) => o.type === 'tower') : [];
  const wanted = Math.max(hunt.participants || 6, 4);
  const participantIds = PARTICIPANT_POOL.slice(0, Math.min(wanted, PARTICIPANT_POOL.length)).map((p) => p.id);
  const leaders = PARTICIPANT_POOL.slice(1, 2).map((p) => p.id);
  const driveAStands = towers.slice(0, Math.min(4, towers.length)).map((o, i) => ({
    id: `stA${i}`,
    pinId: o.id,
    assignedId: participantIds[i] || null,
  }));
  const driveBStands = towers.slice(0, Math.min(3, towers.length)).map((o, i) => ({
    id: `stB${i}`,
    pinId: o.id,
    assignedId: participantIds[(i + 2) % participantIds.length] || null,
  }));

  return {
    teamMap,
    teamColor: COLORS.primary,
    name: huntDisplayName(hunt),
    teamName: hunt.team,
    teamId: hunt.teamId,
    date: hunt.date,
    endDate: hunt.date,
    startTime: hunt.start,
    endTime: hunt.end,
    location: hunt.location,
    leaders,
    participantIds,
    invitationText: `Hi all,\n\nWe meet at the parking by ${hunt.location.toLowerCase()} at ${hunt.start} for coffee and a safety briefing. Bring orange vest, ear protection and a packed lunch — we'll break around noon.\n\nLet me know if you're bringing a dog.\n\n— ${hunt.invitedBy || 'Hunt leader'}`,
    attachedTeamDocIds: ['d-safety', 'd-map'].filter((id) => SAMPLE_TEAM_DOCS.find((d) => d.id === id)),
    attachmentFiles: [],
    drives: [
      { id: 'drA', name: 'Morning push — west ridge', start: hunt.start, end: '10:00', area: 'North parcel', standCount: driveAStands.length, stands: driveAStands },
      { id: 'drB', name: 'Marsh drive',               start: '10:30',    end: hunt.end, area: 'The marsh',    standCount: driveBStands.length, stands: driveBStands },
    ],
    gameIds: game ? [game.id] : [],
    gameClasses: game?.classes
      ? { [game.id]: game.classes.slice(0, 2).map((name) => ({ name, limit: null })) }
      : {},
    joinCode: 'ABC123',
  };
};

// ── Real-data helpers ─────────────────────────────────────────────────────

const AVATAR_COLORS = ['#20502b', '#8a6d3a', '#6b4423', '#3a5a3f', '#a03c16', '#4a5a3a', '#747a76'];

// No `role` on the built participants: the role shown on a hunt is the
// hunt-level one — leader vs. not, not the team role, so a hunt leader reads
// "Hunt leader" rather than "Owner" — and the plan's leader list is already the
// authority on that. Storing a second copy here meant the roster rendered a
// hardcoded English word that no locale could override.
/**
 * Avatar-ready participants, shared by the hunt-detail tabs and the ended-hunt
 * report so both render the same person the same way.
 */
export function buildParticipantPool(participants: HuntPlanParticipant[]): Participant[] {
  return participants.map((p) => {
    const parts = p.userName.trim().split(' ');
    const initials =
      parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : p.userName.slice(0, 2).toUpperCase();
    const colorIdx =
      p.userId.split('').reduce((s, c) => s + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
    return {
      id: p.userId,
      name: p.userName,
      avatar: initials,
      color: AVATAR_COLORS[colorIdx],
      // Carried through so a guest's invited address can show below their name
      // (CLUB-1080); null for a team member.
      invitedEmail: p.invitedEmail ?? null,
      invitedPhone: p.invitedPhone ?? null,
    } as Participant;
  });
}

function enrichFromDetail(detail: HuntPlanDetail): EnrichedPlan {
  const start = new Date(detail.startTime);
  const end = detail.endTime ? new Date(detail.endTime) : start;
  const pad2 = (n: number) => n.toString().padStart(2, '0');
  const date = `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`;
  const endDate = `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`;
  const startFmt = `${pad2(start.getHours())}:${pad2(start.getMinutes())}`;
  const endFmt = `${pad2(end.getHours())}:${pad2(end.getMinutes())}`;

  return {
    teamMap: null,
    teamColor: COLORS.primary,
    name: detail.huntName,
    teamName: detail.groupName,
    teamId: detail.groupId,
    date,
    endDate,
    startTime: startFmt,
    endTime: endFmt,
    location: detail.huntName,
    leaders: detail.participants.filter((p) => p.isLeader).map((p) => p.userId),
    participantIds: detail.participants.map((p) => p.userId),
    invitationText: detail.invitation ?? '',
    attachedTeamDocIds: detail.attachmentDocumentIds ?? [],
    attachmentFiles: detail.attachmentFiles ?? [],
    drives: detail.drives.map((d, i) => ({
      id: `drive-${i}`,
      name: d.name,
      // No start/end: the hunt's own window repeated on every drive read as
      // that drive's time, which nothing records.
      area: '',
      standCount: d.standCount,
      // No coordinates are stored per stand yet, so pinId stays empty (the map
      // needs CLUB-156/157); assignedId drives the per-person assignment list.
      stands: (d.stands ?? []).map((s) => ({
        id: s.id,
        pinId: '',
        assignedId: s.assignedUserId,
        name: s.name ?? null,
      })),
    })),
    gameIds: detail.huntableGames,
    // Classes + per-type limit for the game cards, keyed by species name
    // (real-data gameIds are species names).
    gameClasses: Object.fromEntries(
      (detail.huntableGameDetails ?? []).map((g) => [g.species, g.classes]),
    ),
    joinCode: detail.joinCode ?? '',
  };
}

// ── Props interface ───────────────────────────────────────────────────────

export interface HuntDetailPageProps {
  huntId: string;
  onNavigate: (path: string) => void;
  onClose: () => void;
  detail?: HuntPlanDetail;
  /**
   * Resolve a Firebase Storage object path to a downloadable URL. When
   * provided, attachment-file chips become clickable and open the
   * preview popup; when omitted, chips render as plain labels (the
   * Viltrapport host can leave this null until it wires its own storage
   * resolver).
   */
  onResolveAttachmentUrl?: (storagePath: string) => Promise<string>;
  /**
   * The hunt's attached team documents, already resolved by the host against
   * the team's current documents. The hunt stores only ids, and resolving them
   * needs the team's document listing — which this library has no access to —
   * so the host does the lookup and passes the result in. Omitted (rather than
   * empty) means "no host to ask", which is what the sample-data path relies on.
   */
  teamDocuments?: HuntPlanTeamDocument[];
  /**
   * Persist the caller's RSVP. When provided, the footer composer drives this
   * callback (accept → 'accepted', decline → 'rejected') and, on success, shows
   * the confirmed state in place (it does NOT call onClose). When omitted, the
   * legacy local-only mock behavior is preserved.
   */
  onRespond?: (status: 'accepted' | 'rejected', responseText?: string) => Promise<void>;
  /**
   * Review mode for a completed hunt: shows an "Ended" status instead of the
   * RSVP state and renders read-only (no Accept/Decline/Change-answer — you
   * cannot RSVP to a hunt that already happened). The Overview/Drives/
   * Participants content is the same as the upcoming view.
   */
  ended?: boolean;
  /**
   * Live mode for a hunt in progress. The host (real data) passes this; the
   * mock path also derives it from the sample hunt's status. Shows a live
   * "Ongoing" status and renders read-only (no RSVP to a hunt already underway).
   */
  ongoing?: boolean;
  /**
   * Action rendered in the header of an ended hunt — in practice campfire's
   * "Replay hunt" button, injected by the app shell.
   *
   * A slot rather than a prop describing the action, because the button belongs to
   * a different domain than this page: clubhouse owns the hunt detail view and must
   * not know that a replay exists, let alone how to route to one. The shell is the
   * only place allowed to know both. Rendered only for `ended` hunts, since there
   * is nothing to replay before a hunt has happened.
   */
  headerAction?: ReactNode;
  /**
   * Header slot for an action on a hunt that has NOT happened yet — e.g. "Edit
   * plan" on a hunt whose invitations already went out. Separate from
   * `headerAction` on purpose: that one is gated to `ended` hunts for the replay
   * button, and hosts pass it unconditionally relying on that gate, so it cannot
   * double as a pre-hunt slot. Rendered only while the hunt is neither ended nor
   * ongoing — a live or finished hunt is no longer a plan you can edit.
   *
   * Also gated on `canEditPlan`: the slot is for an action only some viewers
   * are allowed to take, so the permission lives here rather than in every
   * host that fills it.
   */
  planAction?: ReactNode;
  /**
   * Whether the viewer may edit this hunt's plan. Only the people with edit
   * rights get `planAction`: the hunt's creator and its hunt leaders.
   *
   * Defaults to the hunt's own answer — `detail.isOrganizer` (the creator, as
   * already resolved for the "Not invited" chip) or a `detail.participants`
   * entry that is `isLeader` and matches `currentUserId`. Deriving it here
   * rather than trusting each host keeps one rule in one place; pass it
   * explicitly to override, which the mock path (no `detail` to ask) and any
   * host with a broader rule — a group manager, say — must do.
   */
  canEditPlan?: boolean;
  /**
   * The viewer's user id. Drives the per-pin edit/remove permission
   * (`canManageAll || pin.reporterId === currentUserId`) and the default
   * shooter selection in the add form.
   */
  currentUserId?: string;
  /**
   * The hunt's recorded downed/wounded game pins, shown on the "Downed game"
   * tab (ended hunts only). Location is the web {lat,lon} GeoPt — the host maps
   * the api's {latitude,longitude} across the boundary.
   */
  gamePins?: GamePinView[];
  /** The pins could not be read — see `DownedGameTab.loadFailed`. */
  gamePinsLoadFailed?: boolean;
  /**
   * Whether the viewer may manage every pin (hunt leader / group manager).
   * When false, edit/remove is limited to the viewer's own pins.
   */
  canManageAll?: boolean;
  /**
   * Default location for a NEW pin — the hunting-ground centroid the host
   * computes. When undefined (no boundary, no existing pins), the Add action is
   * disabled with a hint, since a pin must carry a location.
   */
  defaultLocation?: GeoPt;
  /**
   * The species catalog for the downed-game picker. @wehunt/ui stays
   * framework-pure, so the host resolves the catalog (via `useGameNamesCatalog`)
   * and passes it in. Only species carrying both a wire base name and a
   * gameType are actually offered (see `selectableDownedGameSpecies`).
   */
  speciesCatalog?: readonly HuntableGameSpecies[];
  /** Persist a new game pin. When omitted, the Add affordance is hidden. */
  onCreateGamePin?: (input: GamePinInput) => Promise<void>;
  /** Persist edits to an existing game pin. */
  onUpdateGamePin?: (id: string, input: GamePinInput) => Promise<void>;
  /** Remove a game pin (after the ConfirmModal is confirmed). */
  onDeleteGamePin?: (id: string) => Promise<void>;
  /**
   * The plan's Dogs section, rendered in the Overview tab (CLUB-883). A slot
   * rather than inline data because registering dogs needs the viewer's own
   * dogs and the register/unregister write, which belong to clubhouse — this
   * page owns neither. The host fills it with a `HuntDogsSection` plus its
   * picker. Omitted (the default) renders nothing, which is right for an ended
   * or ongoing hunt, and for the sample-data path.
   */
  dogsSection?: ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────

export function HuntDetailPage({ huntId, onNavigate, onClose, detail, onResolveAttachmentUrl, teamDocuments, onRespond, ended = false, ongoing: ongoingProp = false, headerAction, planAction, canEditPlan, currentUserId, gamePins, gamePinsLoadFailed = false, canManageAll = false, defaultLocation, speciesCatalog, onCreateGamePin, onUpdateGamePin, onDeleteGamePin, dogsSection }: HuntDetailPageProps) {
  const { t, i18n } = useTranslation('huntInvitation');
  const locale = uiLocale(i18n.language);
  const mockHunt = !detail ? (SAMPLE_HUNTS.find((h) => h.id === huntId) as Hunt | undefined) : undefined;
  // A hunt happening right now is read-only (you can't RSVP to a live hunt) but
  // is shown with a live "Ongoing" status rather than the ended/upcoming chip.
  // Driven by the host prop (real data) or derived from the mock hunt.
  const ongoing = !ended && (ongoingProp || (!detail && mockHunt?.status === 'ongoing'));

  const resolvedRsvp: RsvpStatus = detail
    ? normalizeRsvpStatus(detail.inviteStatus)
    : mockHunt?.myRsvp ?? 'pending';

  // The hunt's creator has nothing to answer: creating a plan writes the
  // creator's own invite as already accepted, so a host that offers
  // `onRespond` whenever an invite record exists would otherwise hand the
  // creator Accept/Decline and "Change my answer" on their own hunt.
  const canRespond = !!onRespond && !detail?.isOrganizer;

  const [rsvp, setRsvp] = useState<RsvpStatus>(resolvedRsvp);
  const [editing, setEditing] = useState(
    !ended && !ongoing && (!detail || canRespond) && resolvedRsvp === 'pending',
  );
  const [pendingChoice, setPendingChoice] = useState<'accept' | 'decline' | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<'overview' | 'drives' | 'participants' | 'downedGame'>('overview');

  const plan = useMemo(
    () => (detail ? enrichFromDetail(detail) : mockHunt ? enrichHuntPlan(mockHunt) : null),
    [detail, mockHunt],
  );

  const effectiveParticipantPool = useMemo(
    () => (detail ? buildParticipantPool(detail.participants) : PARTICIPANT_POOL),
    [detail],
  );

  const tone = { ...RSVP_TONE[rsvp], label: t(RSVP_TONE[rsvp].labelKey) };
  // CLUB-272 — a team member viewing a hunt they weren't invited to: no invite
  // record (inviteStatus null) and not the organizer. The view is already
  // read-only (no RSVP/edit — the footer shows only Close); show a neutral
  // "Not invited" chip instead of a misleading "Pending" RSVP chip.
  const notInvited = !!detail && detail.inviteStatus == null && !detail.isOrganizer;
  const statusChip = ongoing
    ? { label: t('status.ongoing'), color: COLORS.onTertiaryFixedVariant, bg: 'rgba(255,104,31,0.12)' }
    : ended
    ? { label: t('status.ended'), color: COLORS.onSurfaceVariant, bg: COLORS.surfaceContainerHighest }
    : notInvited
    ? { label: t('status.notInvited'), color: COLORS.onSurfaceVariant, bg: COLORS.surfaceContainerHighest }
    : tone;
  const invitedByDisplay = detail ? detail.invitedBy : mockHunt?.invitedBy;
  // Who may edit the plan: the hunt's creator and its hunt leaders. `isLeader`
  // is the hunt-level role (see buildParticipantPool), so a team admin who is
  // not leading this hunt does not qualify.
  const mayEditPlan =
    canEditPlan ?? (!!detail && (detail.isOrganizer || viewerLeadsHunt(detail, currentUserId)));

  const submit = async () => {
    if (submitting) return;
    if (!pendingChoice) return;
    const wire = pendingChoice === 'accept' ? 'accepted' : 'rejected';
    const next: RsvpStatus = pendingChoice === 'accept' ? 'accepted' : 'declined';
    if (onRespond) {
      setSubmitting(true);
      try {
        await onRespond(wire, message.trim() || undefined);
      } catch {
        setSubmitting(false);
        return; // stay in edit; consumer surfaces the error
      }
      setSubmitting(false);
      setRsvp(next);
      setEditing(false);
      setPendingChoice(null);
      setMessage('');
      return; // do NOT close — show the confirmed state with "Change my answer"
    }
    if (mockHunt) mockHunt.myRsvp = next;
    setRsvp(next);
    setEditing(false);
    setPendingChoice(null);
    setMessage('');
    onClose();
  };

  const cancelEdit = () => {
    setPendingChoice(null);
    setMessage('');
    if (rsvp !== 'pending') setEditing(false);
  };

  if (!detail && !mockHunt) return null;
  if (!plan) return null;

  return (
    <div
      style={{
        height: 'calc(100vh - 56px - 48px - 32px)',
        minHeight: 520,
        background: COLORS.surfaceContainerLowest,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '20px 22px 16px',
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          // CLUB-580 — the wash behind the RSVP chip must agree with it. The
          // declined case used to fall through to the green primaryContainer,
          // so a decline sat on the same green header as an acceptance.
          background: `linear-gradient(180deg, ${ended ? COLORS.surfaceContainerHighest : ongoing ? 'rgba(255,104,31,0.12)' : rsvp === 'pending' ? COLORS.surfaceContainerHigh : rsvp === 'declined' ? COLORS.errorContainer : COLORS.primaryContainer} 0%, ${COLORS.surfaceContainerLowest} 100%)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
              {/* CLUB-582: no species chip here. It could only ever show
                  huntableGames[0], so a six-species hunt was announced by
                  whichever one happened to be first. The full list lives in the
                  plan's own game section. */}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: statusChip.color,
                  background: statusChip.bg,
                  padding: '3px 8px',
                  borderRadius: 4,
                }}
              >
                {ongoing && <LiveDot size={6} />}
                {statusChip.label}
              </span>
            </div>
            <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, lineHeight: 1.2 }}>
              {plan.name}
            </div>
            <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 2 }}>
              {/* An unnamed hunt is titled by its team, so repeating the team
                  here would stack the same words twice. */}
              {plan.name === plan.teamName ? fmtSchedule(plan, locale) : `${plan.teamName} · ${fmtSchedule(plan, locale)}`}
            </div>
            {!ended && !ongoing && rsvp === 'pending' && invitedByDisplay && (
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.outline,
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="user" size={12} />
                {t('invitedBy')} <b style={{ color: COLORS.onSurface }}>{invitedByDisplay}</b>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {ended && headerAction}
            {!ended && !ongoing && mayEditPlan && planAction}
            <button className="wh-btn-icon" onClick={onClose} title={t('close')}>
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 2, marginTop: 14, marginLeft: -2 }}>
          {[
            { id: 'overview' as const, label: t('tabs.overview'), icon: 'calendar' as const },
            { id: 'drives' as const, label: t('tabs.drives', { count: plan.drives.length }), icon: 'map' as const },
            {
              id: 'participants' as const,
              label: t('tabs.participants', { count: plan.participantIds.length }),
              icon: 'group' as const,
            },
            // The downed-game tab is only meaningful for a hunt that has ended —
            // it records what was actually shot, which isn't known until the
            // hunt is over.
            ...(ended
              ? [{
                  id: 'downedGame' as const,
                  label: t('tabs.downedGame', { count: gamePins?.length ?? 0 }),
                  icon: 'target' as const,
                }]
              : []),
          ].map((tabItem) => {
            const active = tab === tabItem.id;
            return (
              <button
                key={tabItem.id}
                onClick={() => setTab(tabItem.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: 7,
                  background: active ? COLORS.surfaceContainerLowest : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? COLORS.primary : COLORS.outline,
                  cursor: 'pointer',
                }}
              >
                <Icon name={tabItem.icon} size={14} />
                {tabItem.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
        {tab === 'overview' && <OverviewTab plan={plan} hunt={mockHunt ?? null} participantPool={effectiveParticipantPool} onResolveAttachmentUrl={onResolveAttachmentUrl} teamDocuments={teamDocuments} dogsSection={dogsSection} />}
        {tab === 'drives' && <DrivesTab plan={plan} participantPool={effectiveParticipantPool} />}
        {tab === 'participants' && <ParticipantsTab plan={plan} participantPool={effectiveParticipantPool} detail={detail} />}
        {tab === 'downedGame' && (
          <DownedGameTab
            gamePins={gamePins ?? []}
            loadFailed={gamePinsLoadFailed}
            participantPool={effectiveParticipantPool}
            currentUserId={currentUserId}
            canManageAll={canManageAll}
            defaultLocation={defaultLocation}
            speciesCatalog={speciesCatalog ?? []}
            onCreateGamePin={onCreateGamePin}
            onUpdateGamePin={onUpdateGamePin}
            onDeleteGamePin={onDeleteGamePin}
          />
        )}
      </div>

      <div
        style={{
          padding: '14px 22px',
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        {ended || ongoing || (detail && !canRespond) ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="wh-btn wh-btn-primary" onClick={onClose}>
              {t('close')}
            </button>
          </div>
        ) : editing ? (
          <RsvpComposer
            choice={pendingChoice}
            setChoice={setPendingChoice}
            message={message}
            setMessage={setMessage}
            onCancel={cancelEdit}
            onSubmit={submit}
            canCancel={rsvp !== 'pending'}
            submitting={submitting}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: COLORS.outline }}>
              {rsvp === 'accepted' && (
                <span>
                  <Icon name="check" size={13} /> {t('acceptedConfirmation')}
                </span>
              )}
              {rsvp === 'declined' && (
                <span>
                  <Icon name="close" size={13} /> {t('declinedConfirmation')}
                </span>
              )}
            </div>
            <button className="wh-btn wh-btn-outline" onClick={() => setEditing(true)}>
              <Icon name="edit" size={14} />
              {t('changeMyAnswer')}
            </button>
            <button className="wh-btn wh-btn-primary" onClick={onClose}>
              {t('done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

function OverviewTab({ plan, hunt, participantPool, onResolveAttachmentUrl, teamDocuments, dogsSection }: {
  plan: EnrichedPlan;
  hunt: Hunt | null;
  participantPool: Participant[];
  onResolveAttachmentUrl?: (storagePath: string) => Promise<string>;
  teamDocuments?: HuntPlanTeamDocument[];
  dogsSection?: ReactNode;
}) {
  const { t } = useTranslation('huntInvitation');
  const leaderRows = plan.leaders.map((id) => participantPool.find((p) => p.id === id)).filter(Boolean) as Participant[];
  // A hunt references team documents by id; a host that has the team's document
  // listing resolves them and passes them in. Only the sample-data path omits
  // the prop, and it resolves against the sample catalog instead. An id with no
  // match — a document deleted since it was attached, or a listing that hasn't
  // loaded — drops out rather than becoming a chip naming an unopenable file.
  const docLookup: HuntPlanTeamDocument[] = teamDocuments ?? SAMPLE_TEAM_DOCS;
  const docs = plan.attachedTeamDocIds
    .map((id) => docLookup.find((d) => d.id === id))
    .filter(Boolean) as HuntPlanTeamDocument[];
  // One entry per selected game. For real data, gameIds hold plain species
  // names (not mock catalog IDs), so `def` is undefined and we fall back to the
  // name itself for the label.
  const games = plan.gameIds.map((id) => {
    const def = HUNTABLE_GAME.find((g) => g.id === id);
    return {
      id,
      def,
      displayName: def?.name ?? id,
      classes: plan.gameClasses[id] || [],
    };
  });
  const gamesTitle = games.length > 1 ? t('overview.games') : t('overview.huntableGame');

  // Attachment-preview state: file currently open in the popup, plus the
  // resolved URL (null while in-flight). The resolver lives on the host so
  // this component stays decoupled from Firebase Storage.
  const [previewFile, setPreviewFile] = useState<HuntPlanAttachmentFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handlePreviewClick = async (file: HuntPlanAttachmentFile) => {
    if (!onResolveAttachmentUrl) return;
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewError(null);
    try {
      const url = await onResolveAttachmentUrl(file.storagePath);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('overview.resolveError'));
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <Fact icon="clock" label={t('overview.time')} value={`${plan.startTime} – ${plan.endTime}`} />
        <Fact icon="group" label={t('overview.hunters')} value={t('overview.huntersValue', { count: plan.participantIds.length })} />
      </div>

      <Section title={t('overview.joinCode')} subtitle={t('overview.joinCodeSubtitle')}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: COLORS.surface,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: 3,
              color: COLORS.primary,
            }}
          >
            {plan.joinCode}
          </div>
          <button className="wh-btn-icon" title={t('overview.copyCode')}>
            <Icon name="copy" size={14} />
          </button>
        </div>
      </Section>

      <Section title={t('overview.huntLeader')} icon="shield">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {leaderRows.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                background: COLORS.surfaceContainerLowest,
                border: `1px solid ${COLORS.surfaceContainerHighest}`,
                borderRadius: 8,
              }}
            >
              <Avatar initials={p.avatar} photo={p.photo} size={32} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{p.name}</div>
                {/* Everyone in this section is a hunt leader by definition, so
                    show their hunt role — not the derived team-style role. */}
                <div style={{ fontSize: 11, color: COLORS.outline }}>{t('overview.huntLeader')}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title={t('overview.invitation')} icon="edit">
        <DeltaRenderer
          value={plan.invitationText}
          style={{
            padding: '12px 14px',
            background: COLORS.surfaceContainerLowest,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.55,
          }}
        />
        {(docs.length > 0 || plan.attachmentFiles.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {docs.map((d) => {
              // Same rule as the uploaded files below: a chip is only a button
              // when there is something to open behind it.
              const canPreview = !!onResolveAttachmentUrl && !!d.storagePath;
              const docChipStyle = {
                background: COLORS.primaryOpacity12,
                color: COLORS.primary,
                borderColor: 'transparent',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                cursor: canPreview ? 'pointer' : 'default',
              } as const;
              if (canPreview) {
                return (
                  <button
                    key={d.id}
                    type="button"
                    className="wh-chip"
                    style={docChipStyle}
                    title={t('overview.openDoc', { name: d.name })}
                    onClick={() => handlePreviewClick({
                      fileName: d.name,
                      storagePath: d.storagePath!,
                      mimeType: null,
                      sizeBytes: null,
                    })}
                  >
                    <DocIcon kind={d.kind} size={16} />
                    {d.name}
                  </button>
                );
              }
              return (
                <span key={d.id} className="wh-chip" style={docChipStyle} title={d.name}>
                  <DocIcon kind={d.kind} size={16} />
                  {d.name}
                </span>
              );
            })}
            {plan.attachmentFiles.map((f) => {
              const canPreview = !!onResolveAttachmentUrl;
              const chipStyle = {
                background: COLORS.primaryOpacity12,
                color: COLORS.primary,
                borderColor: 'transparent',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                cursor: canPreview ? 'pointer' : 'default',
              } as const;
              if (canPreview) {
                return (
                  <button
                    key={f.storagePath}
                    type="button"
                    className="wh-chip"
                    style={chipStyle}
                    title={t('overview.previewFile', { name: f.fileName })}
                    onClick={() => handlePreviewClick(f)}
                  >
                    <DocIcon kind={kindFromFileName(f.fileName)} size={16} />
                    {f.fileName}
                  </button>
                );
              }
              return (
                <span
                  key={f.storagePath}
                  className="wh-chip"
                  style={chipStyle}
                  title={f.fileName}
                >
                  <DocIcon kind={kindFromFileName(f.fileName)} size={16} />
                  {f.fileName}
                </span>
              );
            })}
          </div>
        )}
      </Section>

      {games.length > 0 && (
        <Section title={gamesTitle} icon="target">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {games.map(({ id, def, displayName, classes }) => (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: COLORS.surfaceContainerLowest,
                  border: `1px solid ${COLORS.surfaceContainerHighest}`,
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{displayName}</div>
                  {def
                    ? <div style={{ fontSize: 11, color: COLORS.outline }}>{t('overview.season', { season: def.season })}</div>
                    : classes.length === 0 && <div style={{ fontSize: 11, color: COLORS.outline }}>{t('overview.allClasses')}</div>}
                </div>
                {classes.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* One chip per class; a class with a quota shows "· max N". */}
                    {classes.map((c) =>
                      c.limit != null ? (
                        <span
                          key={c.name}
                          className="wh-chip"
                          style={{ fontSize: 10, background: COLORS.primaryContainer, color: COLORS.primary, fontWeight: 700 }}
                        >
                          {t('overview.classMax', { cls: c.name, limit: c.limit })}
                        </span>
                      ) : (
                        <span key={c.name} className="wh-chip" style={{ fontSize: 10 }}>
                          {c.name}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
      {dogsSection}
      {previewFile && (
        <FilePreviewPopup
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
          url={previewUrl}
          error={previewError}
          onClose={closePreview}
        />
      )}
    </div>
  );
}

function DrivesTab({ plan, participantPool }: { plan: EnrichedPlan; participantPool: Participant[] }) {
  const { t } = useTranslation('huntInvitation');
  // Accordion: at most one drive expanded at a time (first open by default).
  const [openId, setOpenId] = useState<string | null>(plan.drives[0]?.id ?? null);
  const nameById = new Map(participantPool.map((p) => [p.id, p.name]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {plan.drives.map((dr, i) => {
        const open = openId === dr.id;
        return (
          <div
            key={dr.id}
            style={{
              background: COLORS.surfaceContainerLowest,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 8,
              borderLeft: `3px solid ${COLORS.primary}`,
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? t('drives.collapse', { name: dr.name }) : t('drives.expand', { name: dr.name })}
              onClick={() => setOpenId(open ? null : dr.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '14px 16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLORS.outline,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {t('drives.drive', { number: i + 1 })}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{dr.name}</div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: COLORS.outline }}>
                  {dr.start && dr.end && `${dr.start} – ${dr.end}`}
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    color: COLORS.outline,
                    transform: open ? 'rotate(180deg)' : undefined,
                    transition: 'transform 0.15s',
                  }}
                >
                  <Icon name="chevron-down" size={16} />
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: COLORS.outline }}>
                {dr.area && (
                  <span>
                    <Icon name="pin" size={11} /> {dr.area}
                  </span>
                )}
                <span>
                  <Icon name="group" size={11} /> {t('drives.standCount', { count: dr.standCount })}
                </span>
              </div>
            </button>
            {open && (
              <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, padding: '12px 16px' }}>
                <DriveStands drive={dr} nameById={nameById} />
                {plan.teamMap && (
                  <div style={{ marginTop: 12 }}>
                    <DriveMapPreview map={plan.teamMap} color={plan.teamColor} drive={dr} />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The stands within an expanded drive, each named as on the team's map (its
 * pin's name, or "Stand N" when the pin is gone or unnamed, as mobile does) and
 * showing the participant assigned to it (or "Unassigned"). Handles the empty cases: a genuinely empty drive (0
 * stands) vs. a hunt that stored only a count but no per-stand detail.
 */
function DriveStands({
  drive,
  nameById,
}: {
  drive: EnrichedPlan['drives'][number];
  nameById: Map<string, string>;
}) {
  const { t } = useTranslation('huntInvitation');
  if (drive.stands.length === 0) {
    const msg =
      drive.standCount > 0
        ? t('stands.detailsUnavailable', { count: drive.standCount })
        : t('stands.noStandsYet');
    return <div style={{ fontSize: 13, color: COLORS.outline }}>{msg}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {drive.stands.map((s, i) => {
        const name = s.assignedId ? nameById.get(s.assignedId) : null;
        return (
          <div
            key={s.id || i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              background: COLORS.surface,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 8,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
              <Icon name="pin" size={12} /> {s.name || t('stands.stand', { number: i + 1 })}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: name ? COLORS.onSurface : COLORS.outline }}>
              {name ?? t('stands.unassigned')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Fixed, like `ReportMap`'s own REPORT_MIN/MAX_ZOOM (see that component's
// comment for the full reasoning): `MapView` reads `minZoom`/`maxZoom` once,
// at construction — well before `useBaseMapTileInfo`'s async `/api/maps`
// fetch can possibly have answered — so deriving these from `tileInfo` can
// never take effect. The catalogue's real fetch bounds still live on the
// raster SOURCE, via `tangerRasterSource`'s `minzoom`/`maxzoom`, which
// `MapView` re-applies on every `tileInfo` change.
const DRIVE_MAP_MIN_ZOOM = 1;
const DRIVE_MAP_MAX_ZOOM = 19;

/** Mirrors `ReportMap`'s own `boundsOf`: a single stand pin (or several at the
 *  same spot) gives a zero-span box, which would fit-bound at the deepest
 *  zoom the tiles have and read as a random patch of forest — padded into a
 *  small neighbourhood instead. */
const DRIVE_MAP_MIN_SPAN_DEG = 0.004;

interface DriveMapBounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

function clampDriveMapLat(lat: number): number {
  return Math.max(-85, Math.min(85, lat));
}
function clampDriveMapLon(lon: number): number {
  return Math.max(-180, Math.min(180, lon));
}

/** The padded extent of a point set, or `null` for none at all. */
function driveMapBoundsOf(points: readonly GeoPt[]): DriveMapBounds | null {
  if (points.length === 0) return null;
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLon = Math.min(minLon, p.lon);
    maxLon = Math.max(maxLon, p.lon);
  }
  const padLat = Math.max(0, (DRIVE_MAP_MIN_SPAN_DEG - (maxLat - minLat)) / 2);
  const padLon = Math.max(0, (DRIVE_MAP_MIN_SPAN_DEG - (maxLon - minLon)) / 2);
  return {
    minLat: clampDriveMapLat(minLat - padLat),
    maxLat: clampDriveMapLat(maxLat + padLat),
    minLon: clampDriveMapLon(minLon - padLon),
    maxLon: clampDriveMapLon(maxLon + padLon),
  };
}

/**
 * Where this drive's stands actually are, on real terrain.
 *
 * This used to be a decorative sketch: a hand-drawn grid pattern, a fake
 * river, and two fake meadow ellipses, with the real ground drawn underneath
 * through the old SVG ground renderer — but projected against a fixed
 * reference centre and zoom (63.27 N, 17.88 E at zoom 12) left over from the
 * prototype's sample data. That reference has no relationship to any real
 * team's location, so a production hunting ground projected off the visible
 * canvas every single time; the only thing anyone ever actually saw here was
 * the decorative terrain, which implied a map that, for real data, was not
 * actually there. This is a real, if
 * non-interactive, `@wehunt/map` view instead, fitted to the drive's own
 * stand pins — the same MapLibre engine every other map surface in this
 * package now runs on.
 */
function DriveMapPreview({
  map,
  color,
  drive,
}: {
  map: TeamMap;
  color: string;
  drive: EnrichedPlan['drives'][number];
}) {
  const huntMapAppearance = useHuntMapAppearance();
  const { t } = useTranslation('huntInvitation');
  const tileInfo = useBaseMapTileInfo();
  const baseSource = useMemo(
    () => (tileInfo ? tangerRasterSource({ ...tileInfo, widths: [] }) : null),
    [tileInfo],
  );

  const standPinIds = useMemo(() => new Set(drive.stands.map((s) => s.pinId)), [drive.stands]);

  // The ground alone — this component draws the pins itself, as `MapMarker`s
  // below, rather than through `TeamMapLayers`' own (interactive-map-shaped)
  // object rendering.
  const groundMap = useMemo<TeamMap>(() => ({ ...map, objects: [], lines: [] }), [map]);

  const standPins = useMemo(
    () =>
      drive.stands
        .map((s) => map.objects.find((o) => o.id === s.pinId))
        .filter((o): o is MapObject => !!o),
    [drive.stands, map.objects],
  );
  const contextPins = useMemo(
    () => map.objects.filter((o) => !standPinIds.has(o.id)),
    [map.objects, standPinIds],
  );

  // Fit to the drive's own stand pins; a drive whose stands carry no
  // coordinates yet (only a stored count — see `enrichFromDetail`'s comment)
  // falls back to the ground's boundary, and a ground with neither falls back
  // to the regional overview rather than fitting to nothing.
  // Border ring points, or a storage-offloaded ground's [SW, NE] bbox when its
  // polygon never came down — shared with the team map (groundFramePoints already
  // applies the ≥3-point border guard and the bbox fallback).
  const boundaryPoints = useMemo(
    () => groundFramePoints(map.boundaryAreas),
    [map.boundaryAreas],
  );
  const framePoints = useMemo(() => {
    if (standPins.length > 0) return standPins.map((p) => ({ lat: p.lat, lon: p.lon }));
    return boundaryPoints;
  }, [standPins, boundaryPoints]);
  const bounds = useMemo(() => driveMapBoundsOf(framePoints), [framePoints]);
  const initialBounds = useMemo<[number, number, number, number] | null>(
    () => (bounds ? [bounds.minLon, bounds.minLat, bounds.maxLon, bounds.maxLat] : null),
    [bounds],
  );

  // Only feeds `TeamMapLayers`' zoom-gated sub-area labels, which is moot
  // here — `subAreaLabels={false}` suppresses the one thing that reads it —
  // so a placeholder value before the map's first view report costs nothing.
  const [liveZoom, setLiveZoom] = useState(MAP_MIN_ZOOM);
  const handleViewChange = useCallback((v: MapViewport) => setLiveZoom(v.zoom), []);

  // Remounts the map whenever the drive's OWN stand-pin coordinates change,
  // so a different drive's preview refits instead of inheriting the previous
  // drive's framing — `MapView` reads `initialBounds`/`initialView` only
  // once, at mount (see `ReportMap`/`StandsMap`'s own comments on this).
  const contentKey = standPins.map((p) => `${p.id}:${p.lat},${p.lon}`).join(';');

  return (
    <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: '#e8efdf' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', minHeight: 240 }}>
        <div style={{ position: 'relative', height: 280, background: '#e8efdf' }}>
          <MapView
            key={contentKey}
            baseSource={baseSource}
            initialBounds={initialBounds}
            initialView={initialBounds ? undefined : EMPTY_MAP_VIEW}
            interactive={false}
            minZoom={DRIVE_MAP_MIN_ZOOM}
            maxZoom={DRIVE_MAP_MAX_ZOOM}
            onViewChange={handleViewChange}
          >
            <TeamMapLayers map={groundMap} appearance={huntMapAppearance} zoom={liveZoom} subAreaLabels={false} />

            {contextPins.map((o) => (
              <MapMarker key={o.id} lat={o.lat} lon={o.lon} anchor="center">
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#9aa39a',
                    border: '1.5px solid #fff',
                    opacity: 0.32,
                  }}
                />
              </MapMarker>
            ))}

            {drive.stands.map((st, i) => {
              const obj = map.objects.find((o) => o.id === st.pinId);
              if (!obj) return null;
              return (
                <MapMarker key={st.id} lat={obj.lat} lon={obj.lon} anchor="center">
                  <div style={{ position: 'relative', width: 22, height: 22 }}>
                    <div
                      style={{
                        position: 'absolute',
                        inset: -6,
                        borderRadius: '50%',
                        border: '2px dashed #a81e8c',
                        opacity: 0.7,
                      }}
                    />
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#a81e8c',
                        border: '2px solid #fff',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </div>
                  </div>
                </MapMarker>
              );
            })}
          </MapView>
        </div>
        <div
          style={{
            background: COLORS.surfaceContainerLowest,
            padding: '12px 14px',
            borderLeft: `1px solid ${COLORS.surfaceContainerHighest}`,
            overflowY: 'auto',
            maxHeight: 320,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            {t('drives.standAssignments')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {drive.stands.map((st, i) => {
              const obj = map.objects.find((o) => o.id === st.pinId);
              const person = PARTICIPANT_POOL.find((p) => p.id === st.assignedId);
              return (
                <div
                  key={st.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: COLORS.primaryContainer,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      flexShrink: 0,
                      background: '#a81e8c',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {obj ? obj.label : t('drives.stand')}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: COLORS.outline,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {person ? (person.you ? t('drives.you') : person.name) : t('stands.unassigned')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ParticipantsTab({ plan, participantPool, detail }: { plan: EnrichedPlan; participantPool: Participant[]; detail?: HuntPlanDetail }) {
  const { t } = useTranslation('huntInvitation');
  const rows = plan.participantIds
    .map((id) => participantPool.find((p) => p.id === id))
    .filter(Boolean) as Participant[];
  // Build rsvp status + note lookups from real detail when available.
  const rsvpById: Record<string, string | null> = {};
  const noteById: Record<string, string | null> = {};
  if (detail) {
    for (const p of detail.participants) {
      rsvpById[p.userId] = p.rsvpStatus;
      noteById[p.userId] = p.responseText ?? null;
    }
  }
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((p) => {
        // Every participant shows a status: a missing/unknown response reads as
        // "Pending" rather than a blank. Mock/standalone (no detail) shows a
        // sensible "Attending" demo default.
        const rawStatus = detail ? rsvpById[p.id] ?? null : 'accepted';
        const style = PARTICIPANT_RSVP_STYLE[normalizeRsvpStatus(rawStatus)];
        const note = detail ? noteById[p.id] ?? null : null;
        const hasNote = !!note && note.trim().length > 0;
        const isExpanded = !!expanded[p.id];
        // The invited address shown below a guest's name (CLUB-1080). `||` not
        // `??` so an empty string counts as absent; and it is shown only when it
        // differs from the name, so a guest who has not signed up yet — whose
        // name line already IS the address — does not show it twice (matching
        // mobile's guard).
        const invitedContact = (p.invitedEmail || p.invitedPhone) || null;
        const showInvitedContact = !!invitedContact && invitedContact !== p.name;
        return (
          <div
            key={p.id}
            style={{
              background: COLORS.surfaceContainerLowest,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
              <Avatar initials={p.avatar} photo={p.photo} size={32} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {p.name}
                  {p.you && (
                    <span
                      className="wh-chip"
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        background: COLORS.primaryOpacity12,
                        color: COLORS.primary,
                        borderColor: 'transparent',
                      }}
                    >
                      {t('participants.you')}
                    </span>
                  )}
                  {plan.leaders.includes(p.id) && (
                    <span className="wh-chip" style={{ fontSize: 10, padding: '1px 6px' }}>
                      {t('participants.leader')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: COLORS.outline }}>
                  {/* A guest invited by email/phone shows the invited address below
                      their name; leadership still reads from the chip above, so the
                      address replaces the role subline. The address is data, not UI
                      copy, so it is not a translated string. */}
                  {showInvitedContact
                    ? invitedContact
                    : plan.leaders.includes(p.id)
                      ? t('participants.roleHuntLeader')
                      : t('participants.roleMember')}
                </div>
              </div>
              <span
                className="wh-chip"
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  background: style.bg,
                  color: style.color,
                  borderColor: 'transparent',
                }}
              >
                {t(style.labelKey)}
              </span>
              {hasNote && (
                <button
                  type="button"
                  className="wh-btn-icon"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? t('participants.hideNote', { name: p.name }) : t('participants.showNote', { name: p.name })}
                  onClick={() => setExpanded((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                  style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                >
                  <Icon name="chevron-down" size={16} />
                </button>
              )}
            </div>
            {hasNote && isExpanded && (
              <div style={{ padding: '0 10px 10px 52px' }}>
                <div style={{ fontSize: 13, color: COLORS.onSurface, fontStyle: 'italic' }}>“{note}”</div>
                <div style={{ fontSize: 11, color: COLORS.outline, marginTop: 4 }}>— {p.name}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Downed game tab ─────────────────────────────────────────────────────────

/**
 * The picker's opening camera when the hunt has NO ground centroid and no
 * existing pin to borrow a location from. The map still needs somewhere to
 * start; a wide overview of the localized markets reads as "pan to the spot"
 * rather than asserting a wrong location. Matches `EMPTY_MAP_VIEW`'s centre.
 */
export const FALLBACK_PIN_LOCATION: GeoPt = { lat: 58.0, lon: 15.0 };

/** Street-level zoom when a real starting location is known, so the ground is
 *  legible enough to nudge the centre pin onto the exact spot. */
const PIN_PICKER_ZOOM = 13;
/** Wide overview zoom for the fallback: nothing to frame, so open on the region
 *  (one step of headroom above the tile source's floor of 4). */
const PIN_PICKER_FALLBACK_ZOOM = 5;

/**
 * The opening camera for the location picker: the pin being edited's own
 * location, else the ground centroid, else a wide regional fallback. Pure so
 * the resolution rule can be tested without a map. Reused to seed the big report
 * map when it enters placement mode.
 */
export function resolvePinPickerView(
  editingLocation: GeoPt | null | undefined,
  defaultLocation: GeoPt | null | undefined,
): MapViewport {
  const center = editingLocation ?? defaultLocation;
  return center
    ? { center, zoom: PIN_PICKER_ZOOM }
    : { center: FALLBACK_PIN_LOCATION, zoom: PIN_PICKER_FALLBACK_ZOOM };
}

/**
 * The live add/edit form's position intent, lifted out of the form so the big
 * report map can host the centre-pin picker. `pinType` chooses which pin art the
 * map draws at its centre; `seedView` is where that map should open. Null when no
 * form is open (the map returns to its normal game-pin view).
 */
export type GamePinDraft = { pinType: GamePinType; seedView: MapViewport };

// The three kinds a hunt report shows, each with its localized label. Observed
// reuses the report card's existing (already-translated) word rather than minting
// a parallel key. A plain binary (wounded ? … : …) silently mislabels observed
// as "Downed", which is what the report-type toggle and the list chip used to do.
const REPORT_TYPE_LABEL_KEY: Record<GamePinType, string> = {
  downed: 'downedGame.typeDowned',
  wounded: 'downedGame.typeWounded',
  observed: 'gameReports.observed',
};

const SEX_KEY: Record<GamePinGender, string> = { male: 'downedGame.sexMale', female: 'downedGame.sexFemale', unknown: 'downedGame.sexUnknown' };
const AGE_KEY: Record<GamePinAgeCategory, string> = { adult: 'downedGame.ageAdult', young: 'downedGame.ageYoung', unknown: 'downedGame.ageUnknown' };
const GENDERS: GamePinGender[] = ['male', 'female', 'unknown'];
const AGES: GamePinAgeCategory[] = ['adult', 'young', 'unknown'];

/** Who shot the animal: the explicit shooter, else the reporter, resolved to a
 * name from the participant pool. */
function resolveShooterName(pool: Participant[], pin: GamePinView, unknownLabel: string): string {
  const id = pin.shooterId ?? pin.reporterId;
  return pool.find((p) => p.id === id)?.name ?? unknownLabel;
}

// A form row carries the animal's FULL data — the wire `name` (species base
// name) and `gameType` — not a catalog lookup key. This is what lets an edit
// round-trip without the live species catalog: `buildGames` reads these fields
// directly and never re-resolves through `selectable`, so an animal whose
// species isn't in the currently-resolved catalog is preserved rather than
// dropped. `gameType: ''` marks a row with no usable species yet (empty catalog
// on Add); such rows are filtered out of the submitted games. `label` is the
// display text shown in the species dropdown for this row.
type DraftRow = {
  name: string;
  gameType: GamePinGameType | '';
  label: string;
  gender: GamePinGender;
  ageCategory: GamePinAgeCategory;
};

/**
 * Add, edit and remove downed game on an ended hunt.
 *
 * Exported because the ended-hunt report renders it too. It is the same shipped
 * component in both places on purpose: the report replaces the tabbed page when
 * its flag is on, and rebuilding this would have meant a second implementation of
 * someone else's working feature — and a second set of permission rules to keep
 * in step. The name still says Tab; it is the tab's component, now also a section.
 */
export function DownedGameTab({
  gamePins,
  participantPool,
  currentUserId,
  canManageAll,
  defaultLocation,
  speciesCatalog,
  loadFailed = false,
  onCreateGamePin,
  onUpdateGamePin,
  onDeleteGamePin,
  hideList = false,
  openForm: controlledOpenForm,
  onOpenFormChange,
  onDraftChange,
  draftLocation,
}: {
  gamePins: GamePinView[];
  participantPool: Participant[];
  currentUserId?: string;
  canManageAll: boolean;
  /**
   * The pins could not be read, as opposed to there being none to read. An
   * empty list and a failed fetch arrive as the same `gamePins` value, so
   * without this the section tells the hunter no game was recorded for a hunt
   * whose game it simply never received.
   */
  loadFailed?: boolean;
  defaultLocation?: GeoPt;
  speciesCatalog: readonly HuntableGameSpecies[];
  onCreateGamePin?: (input: GamePinInput) => Promise<void>;
  onUpdateGamePin?: (id: string, input: GamePinInput) => Promise<void>;
  onDeleteGamePin?: (id: string) => Promise<void>;
  /**
   * Render ONLY the add/edit form machinery (and its delete confirm), never the
   * pin list, the empty/failed message, or the bottom Add button. GameReportsCard
   * turns this on so it can own the list and counts in its own design while
   * reusing this component — and its shipped permission rules — as the editor.
   * Every other caller leaves it false and gets the unchanged tab.
   */
  hideList?: boolean;
  /**
   * Optional controlled form state: null = closed, 'new' = the add form, or a
   * pin id = editing that pin. When provided (with `onOpenFormChange`), an outer
   * component drives which form is open — GameReportsCard opens the add form from
   * its own "Add game" button and the edit form from a row. Omitted leaves the
   * form self-controlled, so existing callers are unchanged.
   */
  openForm?: string | null;
  onOpenFormChange?: (next: string | null) => void;
  /**
   * Reports the live add/edit position intent up to a host that owns a map — the
   * ended-hunt report, whose big left map hosts the centre-pin picker. Called
   * with the draft (pinType + seed camera) whenever a form is open and its
   * pinType changes, and with null when the form closes. Omitted by the plain
   * tab, which has no map to place a pin on.
   */
  onDraftChange?: (draft: GamePinDraft | null) => void;
  /**
   * The location picked on that external map (its live centre). When provided,
   * submit writes the pin here instead of the ground centroid. Omitted by the
   * plain tab, where submit falls back to the edited pin's own location.
   */
  draftLocation?: GeoPt | null;
}) {
  const { t } = useTranslation('huntInvitation');
  const selectable = useMemo(() => selectableDownedGameSpecies(speciesCatalog), [speciesCatalog]);
  // The form drives an external map picker (the report's big map) when the host
  // wired one up; the plain tab has no map, so no location affordance is shown.
  const usesExternalPicker = !!onDraftChange;

  // Which form is open: null = none, 'new' = the add form, or a pin id = edit.
  // Controlled when the caller passes `openForm`; otherwise self-controlled.
  const controlled = controlledOpenForm !== undefined;
  const [openFormState, setOpenFormState] = useState<string | null>(null);
  const openForm = controlled ? (controlledOpenForm as string | null) : openFormState;
  const setOpenForm = useCallback(
    (next: string | null) => {
      if (!controlled) setOpenFormState(next);
      onOpenFormChange?.(next);
    },
    [controlled, onOpenFormChange],
  );
  const [pinType, setPinType] = useState<GamePinType>('downed');
  const [shooterId, setShooterId] = useState<string>('');
  const [comment, setComment] = useState('');
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const defaultShooter = currentUserId ?? participantPool[0]?.id ?? '';
  const canEdit = (pin: GamePinView) => canManageAll || pin.reporterId === currentUserId;
  // A new pin needs a location; without a boundary (and no pins to average) the
  // host passes no default, so adding is blocked with a hint rather than
  // writing a pin with no coordinates.
  const canAdd = !!onCreateGamePin && !!defaultLocation && selectable.length > 0;

  // Seed the form state for a fresh pin / an edited pin. Split out from the
  // start* handlers so the controlled path (GameReportsCard opening a form from
  // the outside, without going through these buttons) seeds identically.
  const seedNewForm = useCallback(() => {
    setPinType('downed');
    setShooterId(defaultShooter);
    setComment('');
    const first = selectable[0];
    setRows([{ name: first?.baseName ?? '', gameType: first?.gameType ?? '', label: first?.name ?? '', gender: 'unknown', ageCategory: 'unknown' }]);
  }, [defaultShooter, selectable]);

  const seedEditForm = useCallback((pin: GamePinView) => {
    setPinType(pin.pinType);
    setShooterId(pin.shooterId ?? pin.reporterId);
    setComment(pin.comment ?? '');
    // Seed each row from the pin's OWN stored animal data — never from the
    // catalog — so editing preserves every animal even when its species is
    // absent from the currently-resolved catalog (offline fallback, or a
    // species retired from Remote Config). The catalog is used only to resolve
    // a nicer display label; the wire name/gameType come straight from the pin.
    setRows(pin.games.map((g) => ({
      name: g.name,
      gameType: g.gameType,
      label: speciesDisplayName(speciesCatalog, g.name),
      gender: g.gender,
      ageCategory: g.ageCategory,
    })));
  }, [speciesCatalog]);

  const startAdd = () => {
    seedNewForm();
    setOpenForm('new');
  };

  const startEdit = (pin: GamePinView) => {
    seedEditForm(pin);
    setOpenForm(pin.id);
  };

  const closeForm = () => {
    setOpenForm(null);
    setRows([]);
  };

  // When the form is opened from OUTSIDE (controlled mode), the start* handlers
  // never ran, so seed here on the transition. Keyed on the opened value so a
  // re-render with the same form open does not clobber in-progress edits.
  const seededFor = useRef<string | null>(openForm);
  useEffect(() => {
    if (seededFor.current === openForm) return;
    seededFor.current = openForm;
    if (openForm === 'new') seedNewForm();
    else if (openForm !== null) {
      const pin = gamePins.find((p) => p.id === openForm);
      if (pin) seedEditForm(pin);
    } else {
      setRows([]);
    }
  }, [openForm, gamePins, seedNewForm, seedEditForm]);

  // Build the submitted games straight from each row's stored name + gameType.
  // No re-resolution through `selectable`, so an animal whose species isn't in
  // the catalog is kept. Rows with no usable species (empty gameType) are the
  // only ones dropped.
  const buildGames = (): GamePinInput['games'] =>
    rows
      .filter((r) => r.name !== '' && r.gameType !== '')
      .map((r) => ({ name: r.name, gender: r.gender, ageCategory: r.ageCategory, gameType: r.gameType as GamePinGameType }));

  const editingPin = openForm && openForm !== 'new' ? gamePins.find((p) => p.id === openForm) : undefined;
  const games = buildGames();
  const canSave = games.length > 0 && !submitting;

  // Report the live draft (open form's pinType + seed camera) up to a host that
  // owns a map, so its big map can host the centre-pin picker. Fires on open, on
  // every pinType change (the icon on the map follows), and clears on close.
  // Kept stable across renders through a ref so this only re-runs on real
  // changes, and reconstructed from primitives so the dep list needs no objects.
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const editLat = editingPin?.location.lat;
  const editLon = editingPin?.location.lon;
  const defLat = defaultLocation?.lat;
  const defLon = defaultLocation?.lon;
  useEffect(() => {
    const report = onDraftChangeRef.current;
    if (!report) return;
    if (openForm === null) {
      report(null);
      return;
    }
    const editLoc = editLat !== undefined && editLon !== undefined ? { lat: editLat, lon: editLon } : undefined;
    const defLoc = defLat !== undefined && defLon !== undefined ? { lat: defLat, lon: defLon } : undefined;
    report({ pinType, seedView: resolvePinPickerView(editLoc, defLoc) });
  }, [openForm, pinType, editLat, editLon, defLat, defLon]);

  // Clear the host's draft if this editor unmounts while a form is still open,
  // so a stale centre pin can't linger on the map.
  useEffect(() => () => onDraftChangeRef.current?.(null), []);

  const submit = async () => {
    if (!canSave) return;
    // The user placed the pin on the host's map (draftLocation). Fall back to the
    // edited pin's own location or the ground centroid when there is no external
    // picker (the plain tab) or it never reported a centre.
    const location = draftLocation ?? editingPin?.location ?? defaultLocation;
    if (!location) return;
    const input: GamePinInput = {
      pinType,
      games,
      location,
      // An observed animal was seen, not shot — never attach a shooter to it, even
      // if one was picked before switching the report type to observed.
      shooterId: pinType === 'observed' ? null : shooterId || null,
      comment: comment.trim() || null,
    };
    setSubmitting(true);
    try {
      if (openForm === 'new') {
        if (!onCreateGamePin) return;
        await onCreateGamePin(input);
      } else if (editingPin && onUpdateGamePin) {
        await onUpdateGamePin(editingPin.id, input);
      }
    } catch {
      setSubmitting(false);
      return; // stay in the form; the host surfaces the error
    }
    setSubmitting(false);
    closeForm();
  };

  const confirmDelete = async () => {
    if (!deletingId || !onDeleteGamePin) return;
    const id = deletingId;
    setDeletingId(null);
    try {
      await onDeleteGamePin(id);
    } catch {
      /* host surfaces the error */
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!hideList && gamePins.length === 0 && openForm !== 'new' && (
        <div style={{ fontSize: 13, color: loadFailed ? COLORS.error : COLORS.outline }}>
          {loadFailed ? t('downedGame.loadFailed') : t('downedGame.empty')}
        </div>
      )}

      {gamePins.map((pin) =>
        openForm === pin.id ? (
          <GamePinForm
            key={pin.id}
            selectable={selectable}
            participantPool={participantPool}
            currentUserId={currentUserId}
            pinType={pinType}
            setPinType={setPinType}
            shooterId={shooterId}
            setShooterId={setShooterId}
            comment={comment}
            setComment={setComment}
            rows={rows}
            setRows={setRows}
            showLocationHint={usesExternalPicker}
            canSave={canSave}
            submitting={submitting}
            onSubmit={submit}
            onCancel={closeForm}
          />
        ) : hideList ? null : (
          <div
            key={pin.id}
            style={{
              padding: '12px 14px',
              background: COLORS.surface,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={`wh-chip ${pin.pinType === 'wounded' ? 'warn' : pin.pinType === 'observed' ? 'muted' : ''}`}>
                {t(REPORT_TYPE_LABEL_KEY[pin.pinType])}
              </span>
              {pin.pinType === 'observed' ? (
                // A sighting has no shooter; keep the row's spacing without a label.
                <span style={{ flex: 1 }} />
              ) : (
                <span style={{ flex: 1, fontSize: 12, color: COLORS.outline }}>
                  {t('downedGame.shooter')}:{' '}
                  <b style={{ color: COLORS.onSurface }}>
                    {resolveShooterName(participantPool, pin, t('downedGame.shooterUnknown'))}
                  </b>
                </span>
              )}
              {canEdit(pin) && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => startEdit(pin)}>
                    {t('downedGame.edit')}
                  </button>
                  <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => setDeletingId(pin.id)}>
                    {t('downedGame.remove')}
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {pin.games.map((g, i) => (
                <div key={i} style={{ fontSize: 13, fontWeight: 600 }}>
                  {speciesDisplayName(speciesCatalog, g.name)}
                  <span style={{ color: COLORS.outline, fontWeight: 400 }}>
                    {' · '}
                    {t(SEX_KEY[g.gender])}
                    {' · '}
                    {t(AGE_KEY[g.ageCategory])}
                  </span>
                </div>
              ))}
            </div>
            {pin.comment && <div style={{ fontSize: 12, color: COLORS.outline }}>{pin.comment}</div>}
          </div>
        ),
      )}

      {openForm === 'new' && (
        <GamePinForm
          selectable={selectable}
          participantPool={participantPool}
          currentUserId={currentUserId}
          pinType={pinType}
          setPinType={setPinType}
          shooterId={shooterId}
          setShooterId={setShooterId}
          comment={comment}
          setComment={setComment}
          rows={rows}
          setRows={setRows}
          showLocationHint={usesExternalPicker}
          canSave={canSave}
          submitting={submitting}
          onSubmit={submit}
          onCancel={closeForm}
        />
      )}

      {!hideList && openForm === null && onCreateGamePin && (
        <div>
          <button
            className="wh-btn wh-btn-primary"
            onClick={startAdd}
            disabled={!canAdd}
            aria-disabled={!canAdd}
          >
            <Icon name="plus" size={16} />
            {t('downedGame.add')}
          </button>
          {/* Explain WHY Add is disabled: no location to attach the pin to, or
              (location fine) no species catalog to pick from. */}
          {!defaultLocation ? (
            <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 6 }}>
              {t('downedGame.noBoundaryHint')}
            </div>
          ) : selectable.length === 0 ? (
            <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 6 }}>
              {t('downedGame.noCatalogHint')}
            </div>
          ) : null}
        </div>
      )}

      {deletingId && (
        <ConfirmModal
          title={t('downedGame.deleteTitle')}
          description={t('downedGame.deleteBody')}
          confirmLabel={t('downedGame.deleteConfirm')}
          cancelLabel={t('downedGame.deleteCancel')}
          tone="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}

/** The add/edit form for a single game pin. Controlled entirely by DownedGameTab. */
function GamePinForm({
  selectable,
  participantPool,
  currentUserId,
  pinType,
  setPinType,
  shooterId,
  setShooterId,
  comment,
  setComment,
  rows,
  setRows,
  showLocationHint,
  canSave,
  submitting,
  onSubmit,
  onCancel,
}: {
  selectable: HuntableGameSpecies[];
  participantPool: Participant[];
  currentUserId?: string;
  pinType: GamePinType;
  setPinType: (t: GamePinType) => void;
  shooterId: string;
  setShooterId: (id: string) => void;
  comment: string;
  setComment: (c: string) => void;
  rows: DraftRow[];
  setRows: (r: DraftRow[]) => void;
  /** Whether to show the "pan the map to place the pin" hint — true only when a
   *  host map is hosting the centre-pin picker (the report page). */
  showLocationHint: boolean;
  canSave: boolean;
  submitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('huntInvitation');
  const updateRow = (i: number, patch: Partial<DraftRow>) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => {
    const first = selectable[0];
    setRows([...rows, { name: first?.baseName ?? '', gameType: first?.gameType ?? '', label: first?.name ?? '', gender: 'unknown', ageCategory: 'unknown' }]);
  };
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));
  // Picking a species from the dropdown sets the row's wire name + gameType +
  // label from the chosen catalog entry. Selecting the row's own pre-existing
  // (non-catalog) option is a no-op — that value isn't in `selectable`, so the
  // row keeps its seeded name/gameType/label.
  const pickSpecies = (i: number, baseName: string) => {
    const sp = selectable.find((s) => s.baseName === baseName);
    if (!sp || !sp.baseName || !sp.gameType) return;
    updateRow(i, { name: sp.baseName, gameType: sp.gameType, label: sp.name });
  };

  // The shooter <select>'s controlled value must always match a rendered
  // option, or the browser shows a different participant than what will be
  // saved. When the selected shooter isn't in the pool (a manager/reporter who
  // isn't a hunt participant, or a departed member on an old pin), prepend a
  // synthetic option for them so value === a visible option.
  const shooterOptions = useMemo(() => {
    const opts = participantPool.map((p) => ({ id: p.id, name: p.name }));
    if (shooterId && !opts.some((o) => o.id === shooterId)) {
      const label = shooterId === currentUserId ? t('participants.you') : t('downedGame.shooterUnknown');
      return [{ id: shooterId, name: label }, ...opts];
    }
    return opts;
  }, [participantPool, shooterId, currentUserId, t]);

  const labelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: COLORS.outline, textTransform: 'uppercase' as const, marginBottom: 4 };

  return (
    <div
      style={{
        padding: '14px',
        background: COLORS.surface,
        border: `1.5px solid ${COLORS.primary}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Report type */}
      <div>
        <div style={labelStyle}>{t('downedGame.reportType')}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['downed', 'observed', 'wounded'] as const).map((pt) => (
            <button
              key={pt}
              onClick={() => setPinType(pt)}
              className={`wh-chip ${pinType === pt ? '' : 'muted'}`}
              style={{ cursor: 'pointer', border: pinType === pt ? `1.5px solid ${COLORS.primary}` : undefined }}
            >
              {t(REPORT_TYPE_LABEL_KEY[pt])}
            </button>
          ))}
        </div>
      </div>

      {/* Shooter — an observed animal was seen, not shot, so it has no shooter. */}
      {pinType !== 'observed' && (
        <div>
          <div style={labelStyle}>{t('downedGame.shooter')}</div>
          <select
            className="wh-select"
            aria-label={t('downedGame.shooter')}
            value={shooterId}
            onChange={(e) => setShooterId(e.target.value)}
            style={{ width: '100%' }}
          >
            {shooterOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Location — the picker lives on the report's big map, not here: its
          centre pin (this pinType) follows the map centre, so the form only tells
          the user to pan that map. Hidden on the plain tab, which has no map. */}
      {showLocationHint && (
        <div>
          <div style={labelStyle}>{t('downedGame.location')}</div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>{t('downedGame.locationHint')}</div>
        </div>
      )}

      {/* Animal rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'flex-end',
              padding: '10px',
              borderRadius: 8,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
            }}
          >
            <div style={{ flex: '1 1 140px', minWidth: 120 }}>
              <div style={labelStyle}>{t('downedGame.species')}</div>
              <select
                className="wh-select"
                aria-label={t('downedGame.species')}
                value={row.name}
                onChange={(e) => pickSpecies(i, e.target.value)}
                style={{ width: '100%' }}
              >
                {row.name === '' && <option value="">{t('downedGame.selectSpecies')}</option>}
                {/* Keep the row's current species visible + selected even when
                    it isn't in the resolved catalog, so an edit never silently
                    changes it. */}
                {row.name !== '' && !selectable.some((s) => s.baseName === row.name) && (
                  <option value={row.name}>{row.label || row.name}</option>
                )}
                {selectable.map((s) => (
                  <option key={s.baseName} value={s.baseName}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: '1 1 90px', minWidth: 80 }}>
              <div style={labelStyle}>{t('downedGame.sex')}</div>
              <select
                className="wh-select"
                aria-label={t('downedGame.sex')}
                value={row.gender}
                onChange={(e) => updateRow(i, { gender: e.target.value as GamePinGender })}
                style={{ width: '100%' }}
              >
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {t(SEX_KEY[g])}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: '1 1 90px', minWidth: 80 }}>
              <div style={labelStyle}>{t('downedGame.age')}</div>
              <select
                className="wh-select"
                aria-label={t('downedGame.age')}
                value={row.ageCategory}
                onChange={(e) => updateRow(i, { ageCategory: e.target.value as GamePinAgeCategory })}
                style={{ width: '100%' }}
              >
                {AGES.map((a) => (
                  <option key={a} value={a}>
                    {t(AGE_KEY[a])}
                  </option>
                ))}
              </select>
            </div>
            {rows.length > 1 && (
              <button
                className="wh-btn-icon"
                onClick={() => removeRow(i)}
                title={t('downedGame.removeAnimal')}
                aria-label={t('downedGame.removeAnimal')}
              >
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
        ))}
        {/* Adding a brand-new animal needs the catalog to pick a species; an
            empty catalog still lets existing rows be edited/kept. */}
        <button
          className="wh-btn wh-btn-text wh-btn-sm"
          onClick={addRow}
          disabled={selectable.length === 0}
          style={{ alignSelf: 'flex-start' }}
        >
          <Icon name="plus" size={14} />
          {t('downedGame.addAnimal')}
        </button>
      </div>

      {/* Comment */}
      <div>
        <div style={labelStyle}>{t('downedGame.comment')}</div>
        <textarea
          className="wh-input"
          placeholder={t('downedGame.commentPlaceholder')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', padding: '10px 14px' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="wh-btn wh-btn-outline" onClick={onCancel} disabled={submitting}>
          {t('downedGame.cancel')}
        </button>
        <button className="wh-btn wh-btn-primary" onClick={onSubmit} disabled={!canSave}>
          {submitting ? t('downedGame.saving') : t('downedGame.save')}
        </button>
      </div>
    </div>
  );
}

// ─── Footer composer ─────────────────────────────────────────────────────────

function RsvpComposer({
  choice,
  setChoice,
  message,
  setMessage,
  onSubmit,
  onCancel,
  canCancel,
  submitting = false,
}: {
  choice: 'accept' | 'decline' | null;
  setChoice: (c: 'accept' | 'decline') => void;
  message: string;
  setMessage: (m: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  canCancel: boolean;
  submitting?: boolean;
}) {
  const { t } = useTranslation('huntInvitation');
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: choice ? 10 : 0 }}>
        <button
          onClick={() => setChoice('decline')}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 8,
            border: `1.5px solid ${choice === 'decline' ? RSVP_COLORS.declined.fg : COLORS.surfaceContainerHighest}`,
            background: choice === 'decline' ? RSVP_COLORS.declined.bg : COLORS.surfaceContainerLowest,
            color: choice === 'decline' ? RSVP_COLORS.declined.fg : COLORS.onSurface,
            fontSize: 14,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={16} />
          {t('composer.decline')}
        </button>
        <button
          onClick={() => setChoice('accept')}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 8,
            border: `1.5px solid ${choice === 'accept' ? COLORS.primary : COLORS.surfaceContainerHighest}`,
            background: choice === 'accept' ? COLORS.primary : COLORS.surfaceContainerLowest,
            color: choice === 'accept' ? COLORS.onPrimary : COLORS.onSurface,
            fontSize: 14,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <Icon name="check" size={16} />
          {t('composer.accept')}
        </button>
      </div>

      {choice && (
        <div>
          <textarea
            className="wh-input"
            placeholder={
              choice === 'accept'
                ? t('composer.acceptPlaceholder')
                : t('composer.declinePlaceholder')
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', height: 'auto', padding: '10px 14px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            {canCancel && (
              <button className="wh-btn wh-btn-outline" onClick={onCancel} disabled={submitting}>
                {t('composer.cancel')}
              </button>
            )}
            <button className="wh-btn wh-btn-primary" onClick={onSubmit} disabled={submitting}>
              {submitting ? t('composer.saving') : t('composer.submitReply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Fact({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        background: COLORS.surface,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: COLORS.outline,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        <Icon name={icon} size={11} />
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{value}</div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        {icon && <Icon name={icon} size={14} />}
        <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: COLORS.outline }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function kindFromFileName(fileName: string): TeamDoc["kind"] {
  const dot = fileName.lastIndexOf(".");
  const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : "";
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx") return "doc";
  if (ext === "xls" || ext === "xlsx") return "xls";
  return "img"; // fallback: closest available DocIcon kind
}
